import { DatePipe, DecimalPipe } from '@angular/common';
import {
  Component,
  computed,
  inject,
  input,
  PipeTransform,
} from '@angular/core';
import { BooleanPipe } from '@core/pipes/boolean.pipe';
import { TableRowDirective } from '@core/primevue/directives/table-row.directive';
import { Difference } from 'microdiff';
import { TableModule } from 'primeng/table';

@Component({
  selector: 'app-letregister-diff',
  imports: [TableModule, TableRowDirective],
  templateUrl: './letregister-diff.component.html',
  styleUrl: './letregister-diff.component.css',
  providers: [DatePipe, BooleanPipe, DecimalPipe],
})
export class LetregisterDiffComponent {
  datePipe = inject(DatePipe);
  booleanPipe = inject(BooleanPipe);
  decimalPipe = inject(DecimalPipe);
  diff = input.required<Difference[]>();

  getDiffTable = computed(() => {
    const diffList = this.diff();
    const list: DiffTableItem[] = [];
    let prevPath: (string | number)[] = [];
    for (const d of diffList) {
      for (let level = 0; level < d.path.length; level++) {
        const fieldname = d.path[level];

        if (fieldname === prevPath[level]) continue;

        const item: DiffTableItem = {
          label: this.getTitle(fieldname),
          level,
          fontWeight: 400,
          fontSize: 1,
        };
        if (level === d.path.length - 1) {
          if (d.type === 'CREATE' || d.type === 'CHANGE') {
            if (d.value instanceof Object) {
              continue;
            }
            item.newValue = this.formatValue(fieldname, d.value);
          }
          if (d.type === 'REMOVE' || d.type === 'CHANGE') {
            if (d.oldValue instanceof Object) {
              continue;
            }
            item.oldValue = this.formatValue(fieldname, d.oldValue);
          }
        } else {
          item.fontWeight = 400 + 100 * (3 - level);
          item.fontSize = 1 + 0.02 * (3 - level);
        }
        list.push(item);
      }

      prevPath = d.path;
    }

    return list;
  });

  getRevisionInfo = computed(() => {
    const diffList = this.diff();
    const revisionInfo = diffList.find(x => x.path[0] === 'revisionNr');
    if (revisionInfo?.type === 'CHANGE') {
      return [revisionInfo.oldValue, revisionInfo.value];
    }
    return [];
  });

  formatValue(key: string | number, value: string) {
    const fieldDef = this.fieldDefitinions[key];
    if (fieldDef) {
      if (fieldDef.formatter) {
        return fieldDef.formatter.transform(value, fieldDef.formatterOptions);
      }
    }
    return value;
  }

  getTitle(name: string | number) {
    const fieldDef = this.fieldDefitinions[name];
    if (fieldDef) {
      return fieldDef.title;
    }

    if (typeof name === 'string') {
      return name.charAt(0).toUpperCase() + name.slice(1).toLowerCase();
    } else {
      return name;
    }
  }

  fieldDefitinions: Record<string, FieldDefinition> = {
    itcLetsikringId: { title: 'ITC Letsikring Id' },
    letsikringNr: { title: 'Letsikrings nr.' },
    eksternReference: { title: 'Ekstern reference' },
    leverandoer: { title: 'Leverandør' },
    kommentar: { title: 'Ændringskommentar' },
    oprettetAf: { title: 'Oprettet af' },
    gaeldendeRevision: { title: 'Revision' },
    revisionNr: { title: 'Revisionsnr' },
    letsikringTilSelvstaendige: { title: 'Letsikring til selvstændige' },
    cvrNr: { title: 'CVR nr' },
    firmaType: { title: 'Selskabsform' },
    firmaRelationsType: { title: 'Firmarelation' },
    tegningsmaade: { title: 'Tegningsmåde' },
    oprettelsesDato: {
      title: 'Ændringsdato',
      formatter: this.datePipe,
      formatterOptions: 'dd-MM-yyyy HH:mm',
    },
    oprettelsesKilde: { title: 'Oprettet via' },
    annulleret: { title: 'Oprettet via', formatter: this.booleanPipe },
    haendelsesKode: { title: 'HændelsesKode' },
    ikrafttraedelse: {
      title: 'Ikrafttrædelse',
      formatter: this.datePipe,
      formatterOptions: 'dd-MM-yyyy',
    },
    opsigelsesDato: {
      title: 'Opsigelsesdato',
      formatter: this.datePipe,
      formatterOptions: 'dd-MM-yyyy',
    },
    tilstand: { title: 'PFA tilstandskode' },
    stopAarsag: { title: 'PFA stop-årsagskode' },
    unisex: { title: 'Unisex', formatter: this.booleanPipe },
    letsikringAfIndtaegt: {
      title: 'Letsikring af indtægt',
      formatter: undefined,
    },
    produktTypeNummer: { title: 'Produkt id' },
    risikoOphoer: {
      title: 'Ophørsdato',
      formatter: this.datePipe,
      formatterOptions: 'dd-MM-yyyy',
    },
    ydelse: {
      title: 'Dækning',
      formatter: this.decimalPipe,
      formatterOptions: '1.2',
    },
    _ydelse: {
      title: 'Dækning',
      formatter: this.decimalPipe,
      formatterOptions: '1.2',
    },
    daekningsstoerrelse: {
      title: 'Dækning',
      formatter: this.decimalPipe,
      formatterOptions: '1.2',
    },
    frekvensForIndbetaling: { title: 'Betalingsfrekvens' },
    praemieFritagelse: {
      title: 'Præmiefritagelse',
      formatter: this.booleanPipe,
    },
    underUdbetaling: { title: 'Under udbetaling', formatter: this.booleanPipe },
    karensMdr: { title: 'Karens', formatter: this.booleanPipe },
    aarligIndkomst: {
      title: 'Årlig indkomst',
      formatter: this.decimalPipe,
      formatterOptions: '1.2',
    },
    betalingViaFrieMidler: {
      title: 'Betaling via frie midler',
      formatter: this.booleanPipe,
    },
    naermestePaaroerendeBegunstigelse: {
      title: 'Nærmeste pårørende begunstigelse',
      formatter: this.booleanPipe,
    },
    barnRisikoOphoerAlder: { title: 'Ophørsalder barn' },
    cprNummer: { title: 'CPR nr' },
    barnNavn: { title: 'Barn navn' },
    forsikredeNavn: { title: 'Forsikrede navn' },
    aarligPris: {
      title: 'Årlig pris',
      formatter: this.decimalPipe,
      formatterOptions: '1.2',
    },
    pris: {
      title: 'Årlig pris',
      formatter: this.decimalPipe,
      formatterOptions: '1.2',
    },
    _pris: {
      title: 'Pris',
      formatter: this.decimalPipe,
      formatterOptions: '1.2',
    },
    opkraevningsBeloeb: { title: 'Straksbetaling' },
    periodeFra: {
      title: 'Periode fra',
      formatter: this.datePipe,
      formatterOptions: 'dd-MM-yyyy',
    },
    periodeTil: {
      title: 'Periode til',
      formatter: this.datePipe,
      formatterOptions: 'dd-MM-yyyy',
    },
    pantsatType: { title: 'Pantsat' },
    signeringsMetode: { title: 'Signeringsmetode' },
    arbejdsgiverCvr: { title: 'Arbejdsgiver CVR nr' },
    arbejdsgiverNavn: { title: 'Arbejdsgiver navn' },
    indbetaltAarTilDato: {
      title: 'Indbetalt år til dato',
      formatter: this.decimalPipe,
      formatterOptions: '1.2',
    },
    opsparetVaerdiKundeKapital: {
      title: 'Opsparet værdi kundekapital',
      formatter: this.decimalPipe,
      formatterOptions: '1.2',
    },
    selvstaendigErhvervsdrivendeOrdning: {
      title: 'Kunden er omfattet af 30% reglerne',
      formatter: this.booleanPipe,
    },
    iBero: {
      title: 'Opsparing er sat i bero',
      formatter: this.booleanPipe,
    },
    indskudMedarbejderBeloeb: {
      title: 'Engangsindskud medarbejder',
      formatter: this.decimalPipe,
      formatterOptions: '1.2',
    },
    indskudArbejdsgiverBeloeb: {
      title: 'Engangsindskud arbejdsgiver',
      formatter: this.decimalPipe,
      formatterOptions: '1.2',
    },
    indskudDato: {
      title: 'Engangsindskud dato',
      formatter: this.datePipe,
      formatterOptions: 'dd-MM-yyyy',
    },
    loebendeNaesteIndbetalingDato: {
      title: 'Løbende indbetaling næste dato',
      formatter: this.datePipe,
      formatterOptions: 'dd-MM-yyyy',
    },
    loebendeSidsteIndbetalingDato: {
      title: 'Sidste indbetaling',
      formatter: this.datePipe,
      formatterOptions: 'dd-MM-yyyy',
    },
    loebendeIndbetalingFrekvens: {
      title: 'Løbende indbetaling frekvens',
    },
    loebendeIndbetalingLaengstMulig: {
      title: 'Indbetales længst muligt',
      formatter: this.booleanPipe,
    },
    loebendeIndbetaling: {
      title: 'Løbende indbetaling',
      formatter: this.booleanPipe,
    },
    loebendeIndbetalingIprocent: {
      title: 'Løbende indbetaling i procent',
      formatter: this.booleanPipe,
    },
    loebendeArbejdsgiverIndbetalingPct: {
      title: 'Løbende indbetaling arbejdsgiver procent',
    },
    loebendeMedarbejderIndbetalingPct: {
      title: 'Løbende indbetaling medarbejder procent',
    },
    loebendeArbejdsgiverIndbetalingBeloeb: {
      title: 'Løbende indbetaling arbejdsgiver beløb',
    },
    loebendeMedarbejderIndbetalingBeloeb: {
      title: 'Løbende indbetaling medarbejder beløb',
    },
    loebendePrivatIndbetalingBeloeb: {
      title: 'Løbende indbetaling privat beløb',
    },
    loebendePrivatIndbetalingRegulering: {
      title: 'Løbende indbetaling privat regulering',
    },
    pensionsgivendeLoen: {
      title: 'Pensionsgivende løn',
      formatter: this.decimalPipe,
      formatterOptions: '1.2',
    },
    overloebFraRatepension: {
      title: 'Overløbsaftale',
      formatter: this.booleanPipe,
    },
    aarligIndbetaling: {
      title: 'Årlig indbetaling',
      formatter: this.decimalPipe,
      formatterOptions: '1.2',
    },
    rateOverloebsgraense: {
      title: 'Tærskelværdi',
      formatter: this.decimalPipe,
      formatterOptions: '1.2',
    },
    overloebsRateKonto: { title: 'Overløbsrate' },
    udbetalingStart: {
      title: 'Udbetaling starter',
      formatter: this.datePipe,
      formatterOptions: 'dd-MM-yyyy',
    },
    tilbagekoebValgt: {
      title: 'Aftalen er tilbagekøbt',
      formatter: this.booleanPipe,
    },
    tilbagekoebDato: {
      title: 'Tilbagekøb dato',
      formatter: this.datePipe,
      formatterOptions: 'dd-MM-yyyy',
    },
    tilbagekoebKonto: { title: 'Tilbagekøb konto' },
    skatteKortType: { title: 'Skattekort type' },
    konverterTilEngangsudbetaling: {
      title: 'Engangsudbetaling',
      formatter: this.booleanPipe,
    },
    simUdbetalingStart: {
      title: 'Simuleret udbetaling starter',
      formatter: this.datePipe,
      formatterOptions: 'dd-MM-yyyy',
    },
    simGarantiPeriode: {
      title: 'Simuleret garanti periode',
      formatterOptions: 'dd-MM-yyyy',
    },
    praemiefritagelse: {
      title: 'Præmiefritagelse',
      formatter: this.booleanPipe,
    },
    depotsikringIndbetalingstype: {
      title: 'Depotsikring indbetalingstype',
    },
    depotsikringSkattekode: {
      title: 'Depotsikring skattekode',
    },
    depotsikringFraDato: {
      title: 'Depotsikring fra dato',
      formatter: this.datePipe,
      formatterOptions: 'dd-MM-yyyy',
    },
    frieMidler: {
      title: 'Frie midler',
      formatter: this.booleanPipe,
    },
    garanti: {
      title: 'Garanti',
      formatter: this.booleanPipe,
    },
    garantiPeriode: {
      title: 'Garanti antal år',
    },
    garantiMaks: {
      title: 'Maks garanti',
      formatter: this.booleanPipe,
    },
    investeringsandelHoejRisiko: {
      title: 'Investeringsandel høj risiko',
    },
    udbetalingssikring: {
      title: 'Udbetalingssikring',
      formatter: this.booleanPipe,
    },
    kundeKapital: {
      title: 'Kundekapital',
      formatter: this.booleanPipe,
    },
    klimaValgt: {
      title: 'Klima valgt',
      formatter: this.booleanPipe,
    },
    investeringsandelKlimaplus: {
      title: 'Klima andel',
    },
    rabatVederlagOverfoerslerProcent: {
      title: 'Rabat på vederlag af overført beløb',
      formatter: this.decimalPipe,
      formatterOptions: '1.1-9',
    },
    rabatVederlagLoebendeIndbetalingerProcent: {
      title: 'Rabat på vederlag af løbende betalinger',
      formatter: this.decimalPipe,
      formatterOptions: '1.1-9',
    },
    rabatVederlagEngangIndbetalingerProcent: {
      title: 'Rabat på vederlag af indskud',
      formatter: this.decimalPipe,
      formatterOptions: '1.1-9',
    },
    saldoRabat: {
      title: 'Rabat på vederlag af saldo',
      formatter: this.booleanPipe,
    },
    synkroniseringStatus: {
      title: 'Synkroniserings status',
    },
    synkroniseringTidspunkt: {
      title: 'Synkroniserings tidspunkt',
      formatter: this.datePipe,
      formatterOptions: 'dd-MM-yyyy HH:mm',
    },
    sendtTilItc: {
      title: 'Sendt til ITC',
      formatter: this.datePipe,
      formatterOptions: 'dd-MM-yyyy HH:mm',
    },
    sendtTilPfa: {
      title: 'Sendt til PFA',
      formatter: this.datePipe,
      formatterOptions: 'dd-MM-yyyy HH:mm',
    },
    indbetalingHeltTilUdbetalingStart: {
      title: 'Indbetaling helt til udbetaling start',
      formatter: this.booleanPipe,
    },
    straksOpkraevning: {
      title: 'Straksopkrævning',
      formatter: this.decimalPipe,
      formatterOptions: '1.2',
    },
    stumppraemie: {
      title: 'Stump præmie',
      formatter: this.decimalPipe,
      formatterOptions: '1.2',
    },
    provision: {
      title: 'Provision',
      formatter: this.decimalPipe,
      formatterOptions: '1.2',
    },
  };
}
interface DiffTableItem {
  label: string | number;
  level: number;
  fontWeight: number;
  fontSize: number;
  oldValue?: string;
  newValue?: string;
}
interface FieldDefinition {
  title: string;
  formatter?: PipeTransform;
  formatterOptions?: string;
}
